import { useId } from 'react';

import {
  canZoom,
  isBelowMinimumSize,
  isPortraitOrNearSquare,
  maxZoomFor,
  placementFor,
} from './photo-crop';
import { PhotoCropBox } from './PhotoCropBox';
import { PhotoCropButtons } from './PhotoCropButtons';
import { PhotoCropNotes } from './PhotoCropNotes';
import { PhotoFileLine } from './PhotoFileLine';
import { PhotoNudgeButtons } from './PhotoNudgeButtons';
import { PhotoZoomControl } from './PhotoZoomControl';
import type { PhotoLayout } from './use-photo-layout';
import type { PhotoSelection } from './use-photo-selection';
import type { VenuePhotoFlow } from './use-venue-photo-flow';

export interface PhotoCropStepProps {
  selection: PhotoSelection;
  flow: VenuePhotoFlow;
  layout: PhotoLayout;
  /** Id of the guidance text shown beside the box. */
  guidanceId: string;
}

/** Frames the chosen photo for the booking site's wide banner before anything is uploaded. */
export function PhotoCropStep({ selection, flow, layout, guidanceId }: PhotoCropStepProps) {
  const notesId = useId();
  const { file, url, size } = selection;
  const zoomable = canZoom(size);
  const maxZoom = zoomable ? maxZoomFor(size) : 1;
  const belowMinimum = isBelowMinimumSize(size);
  const place = placementFor(size, flow.zoom, flow.pan);

  return (
    <div>
      <PhotoCropBox
        url={url}
        size={size}
        zoom={flow.zoom}
        maxZoom={maxZoom}
        pan={flow.pan}
        setZoom={flow.setZoom}
        setPan={flow.setPan}
        describedBy={`${guidanceId} ${notesId}`}
        focusRequested={flow.focusTarget === 'crop'}
        onFocused={flow.focusDone}
      />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 10 }}>
        <PhotoFileLine file={file} />
        <PhotoZoomControl
          zoom={flow.zoom}
          maxZoom={maxZoom}
          enabled={zoomable}
          phone={layout.phone}
          onChange={flow.setZoom}
        />
        <PhotoNudgeButtons
          canMoveSideways={place.widthPct > 100.5}
          canMoveUpDown={place.heightPct > 100.5}
          phone={layout.phone}
          setPan={flow.setPan}
        />
        <PhotoCropNotes
          id={notesId}
          belowMinimum={belowMinimum}
          tooSmallToZoom={!zoomable}
          portrait={isPortraitOrNearSquare(size)}
        />
        <PhotoCropButtons
          phone={layout.phone}
          buttonHeight={layout.buttonHeight}
          saveDisabled={belowMinimum}
          onSave={() => void flow.save()}
          onCancel={flow.cancelCrop}
        />
      </div>
    </div>
  );
}
